/* eslint-disable no-unused-vars */

import React, { useCallback, useMemo, memo, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import useTabVisibility from '@hooks/useTabVisibility';

import RefreshModal from '@components/modal/variants/refresh-data-modal';
import UpdateAppModal from '@components/modal/variants/update-app-modal';

import { getAppDispatch } from '@utilities/dispatch.util';
import { cacheActions } from '@redux/slices/cache';
import { settingsActions } from '@redux/slices/settings';
import SettingsService from '@services/settings.service';
import { globalProgressBarAction } from '@redux/slices/global-progress-bar';
import CancelAndRetryModal from '@components/modal/variants/cancel-and-retry';
import AuthService from '@services/auth.service';
import { homeActions } from '@redux/slices/home';
import OfflineModal from '@components/modal/variants/offline-modal';
import MigrationSelector from 'selectors/migration';

const RefreshingUpdatingModals = ({
  isCaching = false,
  isCachingComplete = false,
  isCachingFailure = false,
  isSyncingModal,
  initialLogin
}) => {
  const [searchParams] = useSearchParams();
  const [openCancelAndRetryModal, setOpenCancelAndRetryModal] = useState(false);
  const [titleCount, setTitleCount] = useState(0);
  const [status, setStatus] = useState('');
  const { lastSync } = useSelector((state) => state.settings);
  const { pendingSync } = useSelector((state) => state.cache);

  const dispatch = getAppDispatch?.();

  const {
    isAppUpdatable = false,
    isAppUpdating = false,
    isUpdatingComplete = false,
    isUpdateFailed = false,
    movingTasksAssetsReduxToIndexDBInprogress = false
  } = useSelector((state) => state.settings);
  const location = useLocation();
  const navigate = useNavigate();

  const setOpenModalEvent = async () => {
    localStorage.removeItem('syncingAndRefreshingStart');
    // const redirectURL = searchParams.get('redirectURL') || '';
    isCachingComplete && dispatch?.(cacheActions.updateRefreshingAndSyncingData(false));
    isCachingComplete && dispatch?.(globalProgressBarAction.setLoadStatus(0));
    dispatch?.(cacheActions.closeCachingComplete());
    setOpenCancelAndRetryModal(false);
    // if (redirectURL) {
    //   navigate(redirectURL);
    // } else {

    const searchParams1 = new URLSearchParams(location.search);

    // Check if "initialLogin=true" is present
    if (searchParams1?.get('initialLogin') === 'true') {
      // Remove all query parameters by navigating only to pathname
      navigate(location.pathname, { replace: true, state: { initialLogin: false } });
    }
    localStorage.removeItem('cachingFailure');

    if (localStorage.getItem('migrationIsDone') === 'true') {
      setTimeout(async () => {
        try {
          await MigrationSelector.createMigrationOldDBToNewDB();
          localStorage.removeItem('migrationIsDone');
          localStorage.setItem('changeDBAfterMigration', 'true');
          localStorage.setItem('deleteDB', 'true');
          window.location.reload();
        } catch (error) {
          console.log({ error });
          localStorage.removeItem('migrationIsDone');
          localStorage.setItem('deleteDB', 'true');
        }
      }, 3000);
    }
  };

  const openUpdateAllModal = useMemo(() => {
    return (
      (isAppUpdatable || isAppUpdating || isUpdatingComplete || isUpdateFailed) &&
      !isCaching &&
      !isCachingComplete &&
      !isCachingFailure
    );
  }, [
    isAppUpdatable,
    isAppUpdating,
    isUpdatingComplete,
    isUpdateFailed,
    isCaching,
    isCachingComplete,
    isCachingFailure
  ]);
  const setOpenUpdateAppModalEvent = useCallback(
    (openState, reload) => {
      if (!openState && reload) localStorage.setItem('getNewDataAfterUpdate', true);

      dispatch?.(
        settingsActions.setAppUpdateStatus({
          isAppUpdatable: false,
          isAppUpdating: false,
          isUpdatingComplete: false,
          isUpdateFailed: false
        })
      );
      if (reload)
        setTimeout(() => {
          window.location.reload();
        }, 3000);

      if (!reload) {
        // app update comes,but you say maybe later
        localStorage.setItem('updateComesButCancelIt', true);
      } else {
        localStorage.setItem('updateComesButCancelIt', false);
      }
    },
    [dispatch, isAppUpdatable]
  );

  const cancelForGettingDataEvent = useCallback(() => {
    window.stop();
    dispatch?.(cacheActions.failureCaching());
    dispatch?.(cacheActions.updateRefreshingAndSyncingData(false));
  }, [dispatch]);

  const cancelAndRetryEvent = () => {
    setOpenCancelAndRetryModal(true);
  };
  const logoutUserAndResetDataEvent = () => {
    window.stop();
    isCachingComplete && dispatch?.(cacheActions.updateRefreshingAndSyncingData(false));
    isCachingComplete && dispatch?.(globalProgressBarAction.setLoadStatus(0));
    dispatch?.(homeActions.setInitialCache(false));
    dispatch?.(cacheActions.closeCachingComplete());
    dispatch?.(cacheActions.failureCaching());
    AuthService.logout({ clearCache: false });
    setOpenCancelAndRetryModal(false);
  };

  return (
    <>
      {isCaching && (
        <RefreshModal
          open={isCaching}
          refreshingData
          setOpen={() => {}}
          cancelForGettingDataEvent={cancelForGettingDataEvent}
          initialLogin={initialLogin}
          cancelAndRetryEvent={cancelAndRetryEvent}
          titleCount={titleCount}
          setTitleCount={setTitleCount}
          setStatus={setStatus}
        />
      )}

      {(isCachingComplete || isCachingFailure) && (
        <RefreshModal
          open={isCachingComplete || isCachingFailure}
          refreshComplete={isCachingComplete}
          refreshFailed={isCachingFailure}
          setOpen={setOpenModalEvent}
          titleCount={titleCount}
          initialLogin={initialLogin}
          setTitleCount={setTitleCount}
          logoutUserAndResetDataEvent={logoutUserAndResetDataEvent}
          setStatus={setStatus}
        />
      )}
      {!isSyncingModal && (
        <UpdateAppModal
          open={openUpdateAllModal}
          setOpen={setOpenUpdateAppModalEvent}
          updateHandler={async () => {
            await SettingsService.updateApp();
          }}
          updateApp={isAppUpdatable}
          updatingApp={isAppUpdating}
          updateComplete={isUpdatingComplete}
          updateFailed={isUpdateFailed}
          movingTasksAssetsReduxToIndexDBInprogress={movingTasksAssetsReduxToIndexDBInprogress}
        />
      )}

      {openCancelAndRetryModal && !isCachingComplete && !isCachingFailure && (
        <CancelAndRetryModal
          open={openCancelAndRetryModal}
          setOpen={setOpenCancelAndRetryModal}
          logoutHandler={logoutUserAndResetDataEvent}
        />
      )}

      {status === 'offline' && !initialLogin && (
        <OfflineModal
          open={status ? true : false}
          setOpen={() => setStatus('')}
          lastSyncTime={moment(lastSync).fromNow(true)}
          pendingSync={pendingSync}
        />
      )}
    </>
  );
};

export default RefreshingUpdatingModals;
